import { useTranslation } from '@lib/useTypedTranslation';

import { formatValue, getFormatNumberToLocale } from '../../../../../../components/data-table/dataFormatters';
import { ITableColumn } from '../../../../../../components/data-table/types';
import { ITranslationKeys } from '../../../../../../components/i18n/keys';
import { useLanguageAndLocaleContext } from '../../../../../../context/languageAndLocale';

// The Process Name/Version/Product Name/Version columns are shared by every process event type's
// Summary table (processHung, processCrashed, ...) - countColumnTitleKey lets each caller title
// its own Number of <Events> column (e.g. NUMBER_OF_HANGS, NUMBER_OF_CRASHES).
export const useProcessEventsSummaryColumns = (countColumnTitleKey: keyof ITranslationKeys['anomalyDetectionBeta']) => {
  const { t } = useTranslation(['anomalyDetectionBeta', 'deviceProcesses']);
  const { locale } = useLanguageAndLocaleContext();

  // This table's own short content (unlike every other wider/multi-column table sharing this
  // component) leaves enough leftover space for table-layout: auto to stretch it into the
  // columns - and since only the two version columns and count are pinned to content-width (see
  // below), that leftover space goes entirely into Process Name/Product Name, the two columns
  // most naturally suited to it (variable-length identifiers, the way a primary Name column
  // commonly gets extra breathing room). Pinning every column instead just reintroduces the
  // original problem one level up: table-layout: auto still stretches the *table* itself to fill
  // its container regardless, so the leftover space has to live somewhere, and spreading it
  // through all five columns evenly can squeeze Product Name (the longest value, e.g. "Microsoft®
  // Windows® Operating System") below its own content width and truncate it with an ellipsis.
  const columns: ITableColumn[] = [
    { id: 'processName', title: t('PROCESS_NAME', { ns: 'deviceProcesses' }), dataPath: 'processName', sortable: true, processor: formatValue },
    // width: '1%' (see IColumn.width) keeps this column snug against Process Name instead of
    // stretching - version strings are a fixed, predictable shape so there's no truncation risk.
    { id: 'processVersion', title: t('PROCESS_VERSION', { ns: 'anomalyDetectionBeta' }), dataPath: 'fileVersion', sortable: true, processor: formatValue, width: '1%' },
    { id: 'productName', title: t('PRODUCT_NAME', { ns: 'deviceProcesses' }), dataPath: 'productName', sortable: true, processor: formatValue },
    { id: 'productVersion', title: t('PRODUCT_VERSION', { ns: 'deviceProcesses' }), dataPath: 'productVersion', sortable: true, processor: formatValue, width: '1%' },
    // Pinned for the same reason - without it, the right-aligned count ends up floating far from
    // the Product Version column rather than sitting snugly right after it.
    { id: 'count', title: t(countColumnTitleKey, { ns: 'anomalyDetectionBeta' }), dataPath: 'count', sortable: true, processor: getFormatNumberToLocale(locale), textAlign: 'right', width: '1%' }
  ];

  return columns;
};
